<template>
  <div>
    <div v-if="this.label" class="custom-select__label">
      {{ label }}
      <span v-if="required" style="color: red;">{{ required ? '*' : '' }}</span>
    </div>
    <q-select
      popup-content-class="ms-dropdown"
      :class="['custom-select', {'custom-select__hideInput': !!this.value && !this.multiple, 'custom-select__fullWidth': !!this.fullWidth}]"
      :options="visibleOptions"
      v-model="model"
      :placeholder="`${this.placeholder || 'Выберите..'} `"
      :use-input="this.useInput !== false"
      :dense="true"
      :map-options="true"
      ref="select"
      outlined
      :multiple="this.multiple"
      :options-dense="true"
      :loading="this.loading"
      :option-value="this.optionValueKey"
      :option-label="this.optionLabelKey || ''"
      dropdown-icon="expand_more"
      @filter="filterFn"
      :rules="this.rules"
      :disable="this.disabled"
    >
      <template v-slot:label>
        <div>
          {{ !noLabel ? `${label || 'Выберите'}` : '' }}
          <span
            v-if="required && !noLabel"
            style="color: red;"
            >{{ required ? '*' : '' }}</span
          >
        </div>
      </template>
      <template
        v-slot:selected
        v-if="this.multiple && (this.computedOptions?.length - 1) === this.value?.length"
      >
        <div v-if="this.value?.length">Все</div>
      </template>
      <template v-slot:selected v-if="this.colorMode">
        <span
          :style="this.selectedItem?.style || {}"
          style="min-height: 15px; width: 100%; border-radius: 4px;"
          >{{ this.selectedItem?.label }}</span
        >
      </template>
      <template v-slot:option="{ itemProps, opt, selected, toggleOption }">
        <q-item
          v-if="!opt.isDivider"
          v-bind="itemProps"
          :disable="!!opt.disable"
          :clickable="!opt.disable"
          :style="opt.style || {}"
        >
          <q-item-section avatar v-if="this.iconKey && opt[this.iconKey]">
            <i class="fa" :class="opt[this.iconKey]"></i>
          </q-item-section>
          <q-item-section>
            <q-item-label
              :style="{ 
            color: opt[this.textColor] || 'inherit', 
            fontWeight: opt.bold || Number(opt.level) === 1 ? 'bold' : 'normal',  
            paddingLeft: Number(opt.level) > 1 ? `${(Number(opt.level) - 1) * 10}px` : '0px' 
            }"
              v-html="opt.label"
            />
          </q-item-section>
          <q-item-section side v-if="this.multiple">
            <q-checkbox
              :model-value="opt[this.optionValueKey] === 'ALL' ? (this.computedOptions?.length - 1) === this.value?.length : selected"
              @update:model-value="toggleOption(opt)"
            />
          </q-item-section>
        </q-item>
        <q-separator v-else class="divider" />
      </template>
      <template v-slot:no-option>
        <q-item>
          <q-item-section
            class="text-grey"
            >{{this.noOptionText || 'Не найдено'}}</q-item-section
          >
        </q-item>
      </template>
      <template v-slot:default v-if="!!this.title">
        <q-tooltip>{{this.title}}</q-tooltip>
      </template>
      <template v-if="value" v-slot:append>
        <q-icon
          name="cancel"
          v-if="(this.multiple ? !!this.value?.length : !!value) && this.clearable !== false"
          @click.stop.prevent="onClearIconClick"
          class="cursor-pointer custom-select__icon"
        />
      </template>
    </q-select>
  </div>
</template>

<script>
export default {
  name: "CustomSelect",
  props: [
    "options",
    "value",
    "onChange",
    "clearHandler",
    "useInput",
    "label",
    "placeholder",
    "required",
    "triggerValidationOnMount",
    "multiple",
    "colorMode",
    "loading",
    "optionLabelKey",
    "optionValueKey",
    "filterFunc",
    "fullWidth",
    "clearable",
    "title",
    "noOptionText",
    "iconKey",
    "textColor",
    "level",
    "noLabel",
    "openOnMount",
    "disabled",
  ],
  data() {
    return {
      searchTerm: "",
      visibleOptions: [],
    };
  },
  watch: {
    computedOptions: {
      immediate: true,
      handler() {
        this.visibleOptions = this.getFilteredOptions(this.searchTerm)
      },
    },
  },
  mounted() {
    if (this.openOnMount)
      this.$refs.select.showPopup()
    if (this.triggerValidationOnMount)
      this.$refs.select.validate()
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(row) {
        let value = row[this.optionValueKey]
        if (this.multiple) {
          if (row?.length) {
            let newValue = row[row?.length - 1][this.optionValueKey]
            if (newValue === 'ALL') {
              const checked = row?.length === this.computedOptions?.length
              if (checked)
                value = []
              else
                value = this.computedOptions?.map(item => item[this.optionValueKey])?.filter(item => item !== 'ALL')
            } else
              value = row?.map(item => typeof item === 'object' ? item[this.optionValueKey] : item)
          } else
            value = []
        }
        this.$emit("change", value, row);
      },
    },
    computedOptions() {
      let options = this.options?.map(item => ({...item, label: item[this.optionLabelKey]}));
      if (this.multiple && options)
        options = [{label: "Выбрать все", [this.optionLabelKey]: 'Все', [this.optionValueKey]: 'ALL'}, ...options]
      return options
    },
    selectedItem() {
      return this.computedOptions?.find(item => item[this.optionValueKey] === this.value);
    },
    rules() {
      const rules = []
      if (this.required) {
        rules.push(val => {
          if (this.multiple) {
            // если multiple — проверяем, что val это массив и он не пустой
            return (Array.isArray(val) && val.length > 0) || 'Это поле обязательно'
          }
          return (!!val) || 'Это поле обязательно'
        })
      }
      return rules
    },
  },
  methods: {
    isDividerOption(item) {
      return item?.isDivider === true || item?.isDivider === 1 || item?.isDivider === 'true'
    },
    optionLabel(item) {
      const label = item?.[this.optionLabelKey] ?? item?.label
      return typeof label === 'string' ? label.toLowerCase() : ''
    },
    dedupeOptions(options) {
      const seen = new Set()
      return options.filter((item) => {
        const key = item?.[this.optionValueKey]
        if (key == null) return true
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },
    getFilteredOptions(searchTerm) {
      const options = this.computedOptions || []
      if (!searchTerm) return this.dedupeOptions(options)

      const term = searchTerm.toLowerCase()
      const matches = (item) => this.optionLabel(item).includes(term)
      const hasLevels = options.some((o) => o.level != null)
      if (!hasLevels) {
        return this.dedupeOptions(options.filter((item) => !this.isDividerOption(item) && matches(item)))
      }

      const result = []
      let i = 0
      while (i < options.length) {
        const item = options[i]
        if (Number(item.level) === 1 && !this.isDividerOption(item)) {
          const parent = item
          const children = []
          i += 1
          while (i < options.length && Number(options[i].level) !== 1 && !this.isDividerOption(options[i])) {
            children.push(options[i])
            i += 1
          }
          const divider = this.isDividerOption(options[i]) ? options[i++] : null
          const parentHit = matches(parent)
          const childHits = children.filter(matches)
          if (parentHit || childHits.length) {
            result.push(parent, ...(parentHit ? children : childHits))
            if (divider) result.push(divider)
          }
        } else {
          if (!this.isDividerOption(item) && matches(item)) result.push(item)
          i += 1
        }
      }
      return this.dedupeOptions(result)
    },
    onClearIconClick() {
      const val = this.multiple ? [] : ""
      if (typeof this.clearHandler === "function") {
        this.clearHandler(val)
      } else if (typeof this.onChange === "function") {
        this.onChange(val)
      }
    },
    filterFn(val, update) {
      update(() => {
        this.searchTerm = val
        this.visibleOptions = this.getFilteredOptions(val)
      });
      if (!!this.filterFunc) {
        this.filterFunc(val, update)
      }
    },
  },
};
</script>

<style lang="scss">
.custom-select {
  font-size: 14px;
  padding-bottom: 0 !important;

  & .q-field__input::placeholder {
    color: #9CA3AF;
    font-size: 12px;
  }

  &__hideInput .q-field__input {
    display: none !important;
  }

  & .q-field__native span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 13px;
  }

  & .q-select__dropdown-icon img {
    width: 10px;
  }

  & .q-field__control {
    &::before {
      border: 1px solid #D1D5DB !important;
      border-radius: 8px !important;
    }
    &::after {
      border-radius: 8px !important;
    }
  }

  & .q-field__bottom {
    padding-top: 3px !important;
  }

  &__label {
    font-size: 13px;
    line-height: 13px;
    margin-bottom: 8px;
  }

  &__icon {
    font-size: 19px !important;
  }

  &__fullWidth {
    width: 100%;
  }
}
.divider {
  margin: 9px 0 !important;
}
</style>
